import './LayerManagers.css';
import React, { useEffect, useState } from 'react';
import { OrderList } from 'primereact/orderlist';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { Dialog } from 'primereact/dialog';
import ImageWMS from 'ol/source/ImageWMS';

import { ServerTypeHelper } from '../../_config/helpers';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

function LayerManager({ map }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(true);
  const [expandedLegend, setExpandedLegend] = useState(null);
  const [activeItemId, setActiveItemId] = useState(null);

  const collapsedLeft = 5;

  const getSidebarWidth = () => {
    return window.innerWidth <= 1280 ? 290 : 320;
  };

  useEffect(() => {
    const handleResize = () => setSidebarWidth(getSidebarWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [sidebarWidth, setSidebarWidth] = useState(getSidebarWidth());

  const ignoredIds = ['6', '10'];
  const ignoredTitles = [
    'imagem de satélite', 'ruas', 'híbrido: imagem de satélite e ruas',
    'esri', 'mapa topográfico', 'mapa global', 'logradouros',
    'open street maps', 'vias', 'csv', 'camada 1', 'camada 2', 'camada 4', 'grade', 'Mapa escuro', 'Mapa global claro'
  ];

  const layerEnable = 'Limites Municipais (IPECE_2021)';

  const loadVisibleLayers = () => {
    if (!map) return;

    const visibleLayers = map.getLayers().getArray()
      .filter(layer => layer.getVisible())
      .map((layer, index) => ({
        id: String(layer.get('id') ?? layer.ol_uid ?? index),
        title: (layer.get('title') ?? layer.get('name') ?? `Camada ${index}`).trim(),
        layer,
        zIndex: layer.getZIndex() ?? index
      }))
      .filter(l =>
        !ignoredTitles.map(t => t.toLowerCase()).includes(l.title.toLowerCase()) &&
        !ignoredIds.includes(l.id)
      )
      .sort((a, b) => a.zIndex - b.zIndex);

    setItems(visibleLayers);
  };

  useEffect(() => {
    loadVisibleLayers();
    if (!map) return;

    map.getLayers().getArray().forEach(layer =>
      layer.on('change:visible', loadVisibleLayers)
    );
  }, [map]);

  useEffect(() => {
    const blockSliderDrag = (e) => {
      const target = e.target;
      if (
        target?.classList?.contains('LayerOpacity') ||
        target?.closest?.('.OpacityControl')
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('dragstart', blockSliderDrag, true);
    return () =>
      document.removeEventListener('dragstart', blockSliderDrag, true);
  }, []);

  const applyZIndex = (list) => {
    const maxZ = Math.max(...list.map(i => i.layer.getZIndex() ?? 0));

    list.forEach((item, index) => {
      const newZ = maxZ - index;
      item.layer.setZIndex(newZ);
      item.zIndex = newZ;
    });
  };

  const onReorder = (event) => {
    const reordered = event.value;
    if (!reordered?.length) return;

    applyZIndex(reordered);
    setItems([...reordered]);
  };

  const getLegendUrl = (layer) => {
    if (!layer || !layer.getVisible()) return null;

    if (layer.get('serverType') === ServerTypeHelper.GEOSERVER) {
      const source = new ImageWMS({
        url: layer.get('url'),
        params: { LAYERS: layer.get('wmsName'), TRANSPARENT: true },
        serverType: 'geoserver'
      });

      return source.getLegendUrl(map.getView().getResolution(),
        {
          legend_options: 'fontColor:#02020233;fontSize:10',
          transparent: 'true'
        });
    }

    return null;
  };

  const handleExpandLegend = (item) => {
    setActiveItemId(item.id);
    setExpandedLegend({
      title: item.title,
      url: getLegendUrl(item.layer)
    });
  };

  const itemTemplate = (item) => {
    const header = (
      <span className="LegendTitle">{t(item.title)}</span>
    );

    const disableLiDrag = (e) => {
      const li = e.currentTarget.closest('.p-orderlist-item');
      if (li) li.setAttribute('draggable', 'false');
    };

    const restoreLiDrag = (e) => {
      const li = e.currentTarget.closest('.p-orderlist-item');
      if (li) li.setAttribute('draggable', 'true');
    };

    const icons = (
      <div className="LegendPanelIcons">
        <Button
          icon="pi pi-window-maximize"
          className="BtnExpandLayer"
          tooltip="Expandir legenda"
          tooltipOptions={{ position: 'top' }}
          onClick={(e) => {
            e.stopPropagation();
            handleExpandLegend(item);
          }}
        />

        {item.title !== layerEnable && (
          <Button
            icon="pi pi-times"
            className="BtnRemoveLayer"
            tooltip="Desativar camada"
            tooltipOptions={{ position: 'top' }}
            onClick={(e) => {
              e.stopPropagation();
              if (items.length === 1) {
                setVisible(false);
              }
              item.layer.setVisible(false);
            }}
          />
        )}
      </div>
    );

    const isActive = activeItemId === item.id;

    return (
      <div
        className={`LegendPanelWrapper ${isActive ? 'LegendPanelWrapper--active' : ''}`}
        onClick={() => setActiveItemId(item.id)}
      >
        <Panel
          header={header}
          icons={icons}
          className="LegendPanel"
        >
          <div className="LegendContent">
            <div className="LegendImageWrapper">
              <img
                src={getLegendUrl(item.layer)}
                alt="Legenda"
                className="PopUpLayerLegend"
                onClick={(e) => {
                  e.stopPropagation();
                  handleExpandLegend(item);
                }}
                onError={(e) => { e.target.src = null; }}
              />
            </div>
            <div className="OpacityControl">
              <label>Opacidade</label>
              <input
                type="range"
                className="LayerOpacity"
                min="0"
                max="1"
                step="0.01"
                defaultValue={item.layer.getOpacity()}
                draggable={false}
                onInput={e =>
                  item.layer.setOpacity(parseFloat(e.target.value))
                }
                onPointerDown={(e) => {
                  e.stopPropagation();
                  disableLiDrag(e);
                }}
                onPointerUp={restoreLiDrag}
                onPointerCancel={restoreLiDrag}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  disableLiDrag(e);
                }}
                onMouseUp={restoreLiDrag}
                onMouseLeave={restoreLiDrag}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  disableLiDrag(e);
                }}
                onTouchEnd={restoreLiDrag}
                onClick={e => e.stopPropagation()}
                onDragStart={e => e.preventDefault()}
              />
            </div>
          </div>
        </Panel>
      </div>
    );
  };

  return (
    <>
      <Tooltip target=".BtnSideBarLegend, .BtnRemoveLayer, .BtnExpandLayer" />

      <Button
        icon="pi pi-list"
        className={`BtnSideBarLegend ${visible ? 'open' : 'closed'}`}
        tooltip={visible ? 'Ocultar legendas' : 'Mostrar legendas'}
        tooltipOptions={{ position: 'left' }}
        onClick={() => setVisible(!visible)}
      />

      <Sidebar
        visible={visible}
        position="left"
        className="SideBarLegend"
        showCloseIcon={false}
        modal={false}
        dismissable={false}
        blockScroll={false}
        onHide={() => { }}
      >
        <div className="SideBarHeader">
          <h5>{t('legendPopup.title') || 'Legendas'}</h5>
          <hr />
        </div>

        <OrderList
          value={items}
          onChange={onReorder}
          itemTemplate={itemTemplate}
          className="ListaLegenda"
          dragdrop
        />
      </Sidebar>

      <Dialog
        visible={!!expandedLegend}
        onHide={() => setExpandedLegend(null)}
        header={expandedLegend?.title || 'Legenda'}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        className="ExpandedLegendDialog"
        style={{ width: '60vw', maxWidth: '90vw' }}
        appendTo={document.body}
      >
        <div className="ExpandedLegendContent">
          {expandedLegend?.url ? (
            <img
              src={expandedLegend.url}
              alt={expandedLegend.title}
            />
          ) : (
            <p className="ExpandedLegendEmpty">
              Nenhuma legenda disponível para esta camada.
            </p>
          )}
        </div>
      </Dialog>
    </>
  );
}

export default LayerManager;